import { ChartSettingsT, OrderedColumnsT } from 'business/definitions';
import { FormatedVizData, UnformatedVizData } from './types';
import {
    getBestSuitableRemainingColumn,
    sortChartColumns,
} from 'helpers/chart/initialization/helpers';

import { getCartesianVizOrderedColumns } from 'helpers/chart/initialization/cartesianVizHelpers';
import { getMapVizOrderedColumns } from 'helpers/chart/initialization/mapVizHelpers';
import { getPyramidOrderedColumns } from 'helpers/chart/initialization/pyramidVizHelpers';
import { getTreeMapVizOrderedColumns } from 'helpers/chart/initialization/treeMapVizHelpers';
import { getYearlyComparisonCategoryScale } from 'helpers/yearlyComparison/scale';

export const orderColumnWithContext = (
    context: UnformatedVizData,
): FormatedVizData => {
    const {
        chartData,
        chartColumns,
        context: { queryMetrics, settings, vizType },
    } = context;

    const sortedChartColumns = sortChartColumns(
        chartColumns,
        chartData,
        settings.reverseGroupBy,
    );

    const metricColumn = sortedChartColumns.find((col) => col.isMetric);
    if (!metricColumn)
        return {
            ...context,
            chartColumns: { y: chartColumns[0] },
        };

    switch (vizType) {
        // Number, paragraph and gauge viz types allow one single metric and a data length of one.
        // This means there is only one effective column that should be put as y.
        // Advanced viz type is tackled the same since the information on its display is carried elsewhere
        case 'number':
        case 'paragraph':
        case 'gauge':
        case 'advanced':
            return {
                ...context,
                chartColumns: { y: metricColumn },
            };
        // Pie and donut viz types require an y column, which has to be a metric, and a x column
        case 'pie':
        case 'donut':
            return {
                ...context,
                chartColumns: {
                    y: metricColumn, // This appears to be important to put y first, otherwise it does not work with a year-typed dimension...
                    x: getBestSuitableRemainingColumn(sortedChartColumns, [
                        metricColumn.key,
                    ]),
                },
            };
        case 'map':
            return {
                ...context,
                chartColumns: getMapVizOrderedColumns(
                    sortedChartColumns,
                    metricColumn,
                ),
            };
        case 'pyramid':
            return {
                ...context,
                chartColumns: getPyramidOrderedColumns(chartColumns),
            };
        case 'treemap':
            return {
                ...context,
                chartColumns: getTreeMapVizOrderedColumns(
                    chartColumns,
                    metricColumn,
                ),
            };
        // Cartesian viz types have a common approach and the order of the columns is editable through the conception on the interface.
        case 'bar':
        case 'horizontal_bar':
        case 'polar_bar':
        case 'line':
        case 'scatterplot':
        case 'area':
            return {
                ...context,
                chartColumns: getCartesianVizOrderedColumns(
                    sortedChartColumns,
                    metricColumn,
                    queryMetrics.length > 1,
                    settings.isYearlyComparison
                        ? getYearlyComparisonCategoryScale(chartColumns)
                        : undefined,
                ),
            };
        default: {
            return {
                ...context,
                chartColumns: { y: metricColumn },
            };
        }
    }
};

/**
 * Checks if a given column is displyed on a given axis
 */
const isDimensionOnChartAxis = (
    column: ResponseColumnT,
    chartDimensionKey: keyof OrderedColumnsT,
) => column.displayOnAxis === chartDimensionKey;

/**
 * If the column does not have the displayOnAxis property, we check if it is
 * present in the chart conception settings
 */
const isDimensionOnChartConceptionAxis = (
    column: ResponseColumnT,
    chartDimensionKey: keyof OrderedColumnsT,
    chartConception: ChartSettingsT['chartConception'],
) =>
    chartConception
        ?.filter((col) => col.chartDimensionKey === chartDimensionKey)
        .map((col) => col.id)
        .includes(column.key);
/**
 * This function is used to order the dimensions of the chart according to the
 * chart conception settings.
 * It makes some hypothesis:
 * - the context contains a maximum of 3 columns to be displayed on the chart
 * - the x and z dimensions behaviour is similar: they are tagged using the displayOnAxis property
 * or they are raw (i.e. unmerged) and we can get them using the chartConception settings
 * - the y dimension is the remaining one
 */
export const conceptionChartDimensionOrderingWithContext = (
    context: UnformatedVizData,
): FormatedVizData => {
    const {
        chartColumns,
        context: {
            settings: { chartConception },
        },
    } = context;
    const xDimension = chartColumns.find(
        (column) =>
            isDimensionOnChartAxis(column, 'x') ||
            isDimensionOnChartConceptionAxis(column, 'x', chartConception),
    );
    const zDimension = chartColumns.find(
        (column) =>
            isDimensionOnChartAxis(column, 'z') ||
            isDimensionOnChartConceptionAxis(column, 'z', chartConception),
    );

    const yDimension = chartColumns.find(
        (column) =>
            column.key !== xDimension?.key && column.key !== zDimension?.key,
    );
    if (!yDimension) {
        throw new Error('The y dimension cannot be undefined');
    }
    return {
        ...context,
        chartColumns: {
            x: xDimension,
            y: yDimension,
            z: zDimension,
        },
    };
};
